// Local Storage utility functions for managing data persistence

export const STORAGE_KEYS = {
  PROJECTS: 'raith_projects',
  DEALS: 'raith_deals',
  TAXONOMY_CATEGORIES: 'raith_taxonomy_categories',
  TAXONOMY_ATTRIBUTES: 'raith_taxonomy_attributes',
  TAXONOMY_TAGS: 'raith_taxonomy_tags',
} as const;

// Generic local storage functions
export const getFromStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading from localStorage key "${key}":`, error);
    return defaultValue;
  }
};

export const saveToStorage = <T>(key: string, data: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving to localStorage key "${key}":`, error);
  }
};

export const removeFromStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from localStorage key "${key}":`, error);
  }
};

// Project-specific functions
export const getProjectsFromStorage = () => {
  return getFromStorage(STORAGE_KEYS.PROJECTS, []);
};

export const saveProjectsToStorage = (projects: any[]) => {
  saveToStorage(STORAGE_KEYS.PROJECTS, projects);
};

export const addProjectToStorage = (project: any) => {
  const projects = getProjectsFromStorage();
  const updatedProjects = [...projects, project];
  saveProjectsToStorage(updatedProjects);
  return updatedProjects;
};

export const updateProjectInStorage = (projectId: string, updates: any) => {
  const projects = getProjectsFromStorage();
  const updatedProjects = projects.map((project: any) => 
    project.id === projectId ? { ...project, ...updates } : project
  );
  saveProjectsToStorage(updatedProjects);
  return updatedProjects;
};

export const deleteProjectFromStorage = (projectId: string) => {
  const projects = getProjectsFromStorage();
  const updatedProjects = projects.filter((project: any) => project.id !== projectId);
  saveProjectsToStorage(updatedProjects);
  return updatedProjects;
};

// Deal-specific functions
export const getDealsFromStorage = () => {
  return getFromStorage(STORAGE_KEYS.DEALS, []);
};

export const saveDealsToStorage = (deals: any[]) => {
  saveToStorage(STORAGE_KEYS.DEALS, deals);
};

export const updateDealInStorage = (dealId: string, updates: any) => {
  const deals = getDealsFromStorage();
  const updatedDeals = deals.map((deal: any) => 
    deal.id === dealId ? { ...deal, ...updates } : deal
  );
  saveDealsToStorage(updatedDeals);
  return updatedDeals;
};

// Taxonomy-specific functions
export const getTaxonomyCategoriesFromStorage = () => {
  return getFromStorage(STORAGE_KEYS.TAXONOMY_CATEGORIES, []);
};

export const saveTaxonomyCategoriesToStorage = (categories: any[]) => {
  saveToStorage(STORAGE_KEYS.TAXONOMY_CATEGORIES, categories);
};

export const getTaxonomyAttributesFromStorage = () => {
  return getFromStorage(STORAGE_KEYS.TAXONOMY_ATTRIBUTES, []);
};

export const saveTaxonomyAttributesToStorage = (attributes: any[]) => {
  saveToStorage(STORAGE_KEYS.TAXONOMY_ATTRIBUTES, attributes);
};

export const getTaxonomyTagsFromStorage = () => {
  return getFromStorage(STORAGE_KEYS.TAXONOMY_TAGS, []);
};

export const saveTaxonomyTagsToStorage = (tags: any[]) => {
  saveToStorage(STORAGE_KEYS.TAXONOMY_TAGS, tags);
};

// Initialize storage with mock data if empty
export const initializeStorageWithMockData = async () => {
  // Only initialize if storage is empty
  if (getProjectsFromStorage().length === 0) {
    // Import and save mock data
    const { mockProjects } = await import('../utils/mockData/projects');
    saveProjectsToStorage(mockProjects);
  }
  
  if (getDealsFromStorage().length === 0) {
    const { mockDeals } = await import('../utils/mockData/deals');
    saveDealsToStorage(mockDeals);
  }
  
  if (getTaxonomyCategoriesFromStorage().length === 0) {
    const { taxonomyCategories, taxonomyAttributes, taxonomyTags } = await import('../utils/mockData/taxonomy');
    saveTaxonomyCategoriesToStorage(taxonomyCategories);
    saveTaxonomyAttributesToStorage(taxonomyAttributes);
    saveTaxonomyTagsToStorage(taxonomyTags);
  }
};
